/**
 * App Component
 *
 * This is the main component of the Outlook Add-in application.
 * It sets up the authentication context and routing for the app.
 *
 * The routing logic has been moved to the AppRoutes component for better organization.
 */
import React, { useMemo } from "react";

import { RouterProvider } from "react-router-dom";

import { router } from "@/taskpane/AppRoutes";
import { ThemeProvider } from "@mui/material";
import { theme } from "@/taskpane/utils/muiTheme";
import { AuthProvider } from "@/taskpane/contexts/auth/AuthContext";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import SnackbarProvider from "@/taskpane/components/snackbar-provider/SnackbarProvider";
import { OfficeContextConsumer, OfficeContextProvider } from "@/taskpane/contexts/office/office-context";
import { PollingProvider } from '@/taskpane/contexts/polling/PollingContext';
import { CrispChat } from '@/taskpane/components/crisp-chat/CrispChat';

import { GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = `pdf.worker.min.js`;

export default function App() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          // This will log any errors to the console only once so can can keep track of errors in the console before implementing a improved error handling system.
          onError: (error) =>
            console.error(`Something went wrong: ${error.message}`),
        }),
      }),
    []
  );

  return (
    <QueryClientProvider client={queryClient}>
      <PollingProvider>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <SnackbarProvider>
            <OfficeContextProvider>
              <OfficeContextConsumer>
                {() => (
                  <>
                    {/* <CrispChat /> */}
                    <RouterProvider router={router} />
                  </>
                )}
              </OfficeContextConsumer>
            </OfficeContextProvider>
          </SnackbarProvider>
        </AuthProvider>
      </ThemeProvider>
      </PollingProvider>
    </QueryClientProvider>
  );
}
